/* API-related constants */

import { ApiUtil } from './api-utils'
import { type FailedResponse, type SuccessResponseWithoutPayload } from './types'
import { mapNoticeResponse } from './response-mappers'
import { type NoticeResponseItem } from './notices-api-types'

const ADMIN_NOTICES_API_PATH = '/admin/notices'

/* Response types */

interface SingleNoticeReturningSuccessResponse {
  succeeded: true
  payload: {
    notice: NoticeResponseItem
  }
}

export type CreateNoticeResponse = SingleNoticeReturningSuccessResponse | FailedResponse

export type DeleteNoticeResponse = SuccessResponseWithoutPayload | FailedResponse

/* Export(s) */

/**
 * Backend admin API for managing notices.
 */
export const AdminNoticesApi = {
  createNotice,
  deleteNotice
}

/* API methods */

/**
 * Create a new notice.
 * @param content Notice content.
 * @param token Bearer token.
 */
async function createNotice(content: string, token: string): Promise<CreateNoticeResponse> {
  const apiDesc = 'create notice request'
  const url = ApiUtil.getApiUrl(ADMIN_NOTICES_API_PATH)
  const reqBody = {
    content
  }

  try {
    const response = await ApiUtil.doPostRequest(url, reqBody, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }
    const payload: unknown = await response.json()
    const parsedPayload = mapNoticeResponse(payload)
    return {
      succeeded: true,
      payload: {
        notice: parsedPayload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Delete existing notice.
 * @param noticeId ID of the notice to be deleted.
 * @param token Bearer token.
 */
async function deleteNotice(noticeId: string, token: string): Promise<DeleteNoticeResponse> {
  const apiDesc = 'delete notice request'
  const url = ApiUtil.getApiUrl(`${ADMIN_NOTICES_API_PATH}/${noticeId}`)

  return await ApiUtil.execDeleteRequest(url, token, apiDesc)
}
